import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ADMIN_TEST_HISTORY_API = process.env.REACT_APP_ADMIN_TEST_HISTORY_API || `${API_BASE_URL}/api/admin-app/test-history/`;

const AdminTestHistoryPage = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const headers = useMemo(() => {
        // Try guest_access_token first (for backward compatibility)
        let token = (localStorage.getItem('guest_access_token') || sessionStorage.getItem('guest_access_token') || '').trim();
        
        // If not found, try admin userDetails (from redux-based login)
        if (!token) {
            const userDetailsStr = localStorage.getItem('userDetails');
            if (userDetailsStr) {
                try {
                    const userDetails = JSON.parse(userDetailsStr);
                    token = userDetails.access || '';
                } catch (e) {
                    // JSON parse error, token remains empty
                }
            }
        }
        
        if (!token) {
            return null;
        }

        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        };
    }, []);

    const detailUrl = (id) => `${ADMIN_TEST_HISTORY_API}${id}/`;

    const fetchRows = async () => {
        setLoading(true);
        if (!headers) {
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(ADMIN_TEST_HISTORY_API, { headers });
            const data = response.ok ? await response.json() : [];
            setRows(Array.isArray(data) ? data : []);
        } catch (error) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [headers]);;

    const handleDelete = async (itemId) => {
        if (!headers) {
            setErrorMessage('Session expired. Please log in again.');
            return;
        }

        const result = await Swal.fire({
            title: 'Delete test? ',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
        });
        if (!result.isConfirmed) return;

        setSubmitting(true);
        try {
            const response = await fetch(detailUrl(itemId), {
                method: 'DELETE',
                headers,
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                setErrorMessage(data?.error || 'Unable to delete test.');
                return;
            }
            setSuccessMessage('Test deleted successfully.');
            await fetchRows();
        } catch (error) {
            setErrorMessage('Unable to reach server right now.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="admin-panel-card admin-table-section">
            <div className="admin-college-header">
                <div>
                    <h3>Test History</h3>
                    <p className="admin-subtitle">All test batches created by admin and question managers.</p>
                </div>
            </div>

            {errorMessage ? <div className="admin-security-feedback admin-security-feedback-error">{errorMessage}</div> : null}
            {successMessage ? <div className="admin-security-feedback admin-security-feedback-success">{successMessage}</div> : null}

            <div className="admin-table-wrap">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Requested</th>
                            <th>Selected</th>
                            <th>Total Marks</th>
                            <th>Section Timing</th>
                            <th>Created</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="8">Loading test history...</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan="8">No test history found.</td></tr>
                        ) : (
                            rows.map((row) => {
                                const sections = Array.isArray(row.sections) ? row.sections : [];
                                const timingSummary = sections.length > 0
                                    ? sections.map((s) => `${(s.topic || 'N/A').substring(0, 10)}: ${s.question_count || 0}q, ${s.time_minutes || 0}m`).join(' | ')
                                    : 'No timing configured';
                                return (
                                    <tr key={row.id}>
                                        <td>{row.id}</td>
                                        <td>{row.name || `Test #${row.id}`}</td>
                                        <td>{row.requested_question_count ?? '-'}</td>
                                        <td>{row.selected_question_count ?? '-'}</td>
                                        <td>{row.total_marks ?? '-'}</td>
                                        <td style={{ fontSize: '12px', color: '#6b7280' }}>{timingSummary}</td>
                                        <td>{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</td>
                                        <td>
                                            <div className="admin-row-actions">
                                                <button type="button" className="admin-row-action-btn" onClick={() => window.location.href = `/admin/test-history/edit/${row.id}?role=admin`} disabled={submitting}>Edit</button>
                                                <button type="button" className="admin-row-action-btn admin-row-action-btn-danger" onClick={() => handleDelete(row.id)} disabled={submitting}>Delete</button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminTestHistoryPage;
